import type { AssessmentTaskStatus } from "../services/assessment_tasks.types";

export type CalendarDueState = "overdue" | "due_soon" | null;
export type AssessmentTaskDueState = CalendarDueState;

interface CalendarParts {
    year: number;
    month: number;
    day: number;
}

function parseCalendarDate(value: string | null | undefined): CalendarParts | null {
    const match = typeof value === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
    if (!match) return null;

    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (year < 1000 || month < 1 || month > 12 || day < 1) return null;

    const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
    const daysInMonth = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    return day <= daysInMonth[month - 1] ? { year, month, day } : null;
}

function calendarOrdinal({ year, month, day }: CalendarParts): number {
    const completedYears = year - 1;
    const daysBeforeMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
    const leapDay = month > 2 && (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) ? 1 : 0;
    return completedYears * 365 + Math.floor(completedYears / 4) -
        Math.floor(completedYears / 100) + Math.floor(completedYears / 400) +
        daysBeforeMonth[month - 1] + day + leapDay;
}

export function browserCalendarToday(now: Date = new Date()): string | null {
    if (!(now instanceof Date) || Number.isNaN(now.getTime())) return null;
    const year = now.getFullYear();
    if (year < 1000 || year > 9999) return null;
    return `${String(year).padStart(4, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
}

export function isCalendarDate(value: unknown): value is string {
    return typeof value === "string" && parseCalendarDate(value) !== null;
}

export function isNullableCalendarDate(value: unknown): value is string | null {
    return value === null || isCalendarDate(value);
}

// Keep picker values in the browser's calendar; ISO/UTC conversion can change the selected day.
export function calendarDateToPickerDate(value: string | null): Date | null {
    const parts = value ? parseCalendarDate(value) : null;
    if (!parts) return null;
    const date = new Date(parts.year, parts.month - 1, parts.day);
    return browserCalendarToday(date) === value ? date : null;
}

export function pickerDateToCalendarDate(value: Date | null): string | null {
    return value instanceof Date ? browserCalendarToday(value) : null;
}

export function isPickerDateDisabled(value: Date, unchangedDueDate: string | null): boolean {
    const day = pickerDateToCalendarDate(value);
    const today = browserCalendarToday();
    return !day || !today || (day !== unchangedDueDate && day <= today);
}

export function isAllowedDueDate(value: unknown, unchangedDueDate: string | null): value is string | null {
    if (value === null) return true;
    if (!isCalendarDate(value)) return false;
    if (value === unchangedDueDate) return true;
    const today = browserCalendarToday();
    return today !== null && value > today;
}

export function calendarDaysBetween(fromDate: string, toDate: string): number | null {
    const from = parseCalendarDate(fromDate);
    const to = parseCalendarDate(toDate);
    return from && to ? calendarOrdinal(to) - calendarOrdinal(from) : null;
}

export function calendarDueState(
    dueDate: string | null,
    isFinished: boolean,
    today: string | null = browserCalendarToday()
): CalendarDueState {
    if (isFinished || !dueDate || !today) return null;
    const daysUntilDue = calendarDaysBetween(today, dueDate);
    if (daysUntilDue === null) return null;
    if (daysUntilDue < 0) return "overdue";
    return daysUntilDue <= 7 ? "due_soon" : null;
}

export function assessmentTaskDueState(
    dueDate: string | null,
    status: AssessmentTaskStatus,
    today: string | null = browserCalendarToday()
): AssessmentTaskDueState {
    return calendarDueState(dueDate, status === "approved", today);
}
